import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface Funcionalidade {
  id: string;
  nome: string;
  chave: string;
  descricao: string;
  autorizado: boolean;
}

export interface Recurso {
  id: string;
  nome: string;
  descricao: string;
  funcionalidades: Funcionalidade[];
}

export interface MenuResponse {
  recursos: Recurso[];
}

@Injectable({ providedIn: 'root' })
export class PermissaoService {
  private readonly http = inject(HttpClient);

  /** Chaves autorizadas (ex: "logistica.remessas:find_all") */
  private chavesAutorizadas = signal<Set<string>>(new Set());

  /** Indica se as permissões já foram carregadas */
  carregado = signal(false);

  /**
   * Busca as permissões do SGI e armazena as chaves autorizadas.
   * Chame isso uma única vez no bootstrap da app (ex: APP_INITIALIZER ou no layout principal).
   */
  carregarPermissoes(aplicacaoId: string): Observable<MenuResponse> {
    const url = `${environment.sgiApiUrl}/aplicacoes/${aplicacaoId}/menu`;

    return this.http.get<MenuResponse>(url).pipe(
      tap((response) => {
        const chaves = new Set<string>();

        for (const recurso of response.recursos) {
          for (const func of recurso.funcionalidades) {
            if (func.autorizado) {
              chaves.add(func.chave);
            }
          }
        }
        this.chavesAutorizadas.set(chaves);
        this.carregado.set(true);
      })
    );
  }

  /** Verifica se o usuário tem UMA permissão específica */
  hasPermission(chave: string): boolean {
    return this.chavesAutorizadas().has(chave);
  }

  /** Verifica se o usuário tem QUALQUER UMA das permissões informadas */
  hasAnyPermission(chaves: string[]): boolean {
    return chaves.some((c) => this.chavesAutorizadas().has(c));
  }

  /** Verifica se o usuário tem TODAS as permissões informadas */
  hasAllPermissions(chaves: string[]): boolean {
    return chaves.every((c) => this.chavesAutorizadas().has(c));
  }
}